/**
 * ============================================================================
 * 🔁 Historical Playback Loop
 * ============================================================================
 * Replays the recorded telemetry from /api/playback on a loop when no live
 * ADS-B feed is running, reusing the exact same rendering path as live data
 * (RadarMap.update() doesn't know or care whether a plane object came from
 * /api/live or a replayed frame).
 */

/**
 * Converts one /api/playback frame's aircraft (short field names, raw
 * numeric alt/spd, no aircraft_code/squawk/is_on_ground) into the same
 * shape /api/live's aircraft objects have, so the rest of the app doesn't
 * need to know which mode it's in.
 */
function adaptPlaybackAircraft(a) {
  return {
    registration: a.reg,
    callsign: a.cs,
    flight_number: a.fn,
    airline: a.airline,
    // The playback query only selects aircraft_model, not aircraft_code —
    // in this dataset they're always identical, so it doubles as the ICAO
    // type code the marker icon lookup needs.
    aircraft_model: a.model,
    aircraft_code: a.model,
    altitude_ft: typeof a.alt === 'number' ? `${a.alt.toLocaleString()} ft` : (a.alt || '0 ft'),
    ground_speed_kts: typeof a.spd === 'number' ? `${a.spd} kts` : (a.spd || '0 kts'),
    track_deg: a.trk || '0°',
    vertical_speed_fpm: a.vspd || '0 fpm',
    flight_phase: a.phase || '',
    latitude: a.lat,
    longitude: a.lon,
    squawk: '-',
    is_on_ground: typeof a.alt === 'number' ? a.alt === 0 : false,
    photo: { has_photo: !!a.has_photo }
  };
}

export class PlaybackLoop {
  constructor(apiService) {
    this.api = apiService;
    this.frames = [];
    this.index = 0;
  }

  /** Fetches every recorded frame once. Returns the frame count. */
  async load(stepSeconds = 5) {
    const data = await this.api.getPlayback(stepSeconds);
    this.frames = data.frames || [];
    this.index = 0;
    return this.frames.length;
  }

  /**
   * Returns the current frame's aircraft (adapted to the /api/live shape),
   * then advances to the next frame, wrapping back to the start once the
   * recording ends — a continuous loop rather than a one-shot replay.
   */
  next() {
    if (this.frames.length === 0) return [];
    const frame = this.frames[this.index];
    this.index = (this.index + 1) % this.frames.length;
    return frame.aircraft.map(adaptPlaybackAircraft);
  }
}
